<section class="dictionary" data-testid="dictionary-panel">
  <header class="dictionary__header">
    <div>
      <h3 class="dictionary__title">Dicionário do plano padrão</h3>
      <p class="dictionary__subtitle">
        O texto que o agente de De/Para lê antes de classificar: o que entra e o que não entra em
        cada conta padrão. As propostas do revisor entram sozinhas, cada documento numa versão nova,
        e você mantém ou desfaz.
      </p>
    </div>
    <app-button
      [label]="'Atualizar'"
      [icon]="'pi pi-refresh'"
      [theme]="'white'"
      [type]="'button'"
      [disabled]="isLoading() || isMutating()"
      data-testid="dictionary-refresh"
      (sendButtonClick)="refresh()" />
  </header>

  <app-dictionary-summary-bar
    [toReview]="summary()?.toReview ?? null"
    [pending]="summary()?.pending ?? null"
    [activeVersionNumber]="activeVersion()?.number ?? null"
    [section]="section()"
    (sectionRequested)="setSection($event)" />

  <div class="dictionary__tabs" role="tablist" aria-label="Seções do dicionário">
    <button
      type="button"
      role="tab"
      class="dictionary__tab"
      [class.dictionary__tab--active]="section() === 'review'"
      [attr.aria-selected]="section() === 'review'"
      data-testid="dictionary-tab-review"
      (click)="setSection('review')">
      A revisar
      @if (summary()?.toReview; as count) {
        <span class="dictionary__badge dictionary__badge--attention">{{ count }}</span>
      }
    </button>
    <button
      type="button"
      role="tab"
      class="dictionary__tab"
      [class.dictionary__tab--active]="section() === 'pending'"
      [attr.aria-selected]="section() === 'pending'"
      data-testid="dictionary-tab-pending"
      (click)="setSection('pending')">
      Pendentes
      @if (summary()?.pending; as count) {
        <span class="dictionary__badge" data-testid="dictionary-pending-count">{{ count }}</span>
      }
    </button>
    <button
      type="button"
      role="tab"
      class="dictionary__tab"
      [class.dictionary__tab--active]="section() === 'entries'"
      [attr.aria-selected]="section() === 'entries'"
      data-testid="dictionary-tab-entries"
      (click)="setSection('entries')">
      Verbetes
    </button>
    <button
      type="button"
      role="tab"
      class="dictionary__tab"
      [class.dictionary__tab--active]="section() === 'versions'"
      [attr.aria-selected]="section() === 'versions'"
      data-testid="dictionary-tab-versions"
      (click)="setSection('versions')">
      Histórico
    </button>
  </div>

  @if (!canManage()) {
    <p class="dictionary__readonly">
      Você pode consultar o dicionário. Manter ou desfazer mudanças, aplicar ou recusar propostas,
      editar verbetes e voltar versão exigem a permissão de gerenciar o dicionário.
    </p>
  }

  @switch (section()) {
    @case ('review') {
      <div class="dictionary__section" data-testid="dictionary-section-review">
        <p class="dictionary__help" data-testid="dictionary-help-review">
          Estas propostas do revisor já estão valendo no dicionário.
          <strong>Manter</strong> só tira da lista; <strong>Desfazer</strong> tira o texto do
          dicionário criando uma versão nova, com o seu motivo.
        </p>
        @if (reviewLoading()) {
          <div class="dictionary__loading">
            <app-cog-loading-spinner [size]="64" />
          </div>
        } @else if (reviewError(); as message) {
          <p class="dictionary__error" role="alert">{{ message }}</p>
        } @else if (reviewProposals().length === 0) {
          <p class="dictionary__empty" data-testid="dictionary-review-empty">
            Nada a revisar. As próximas propostas do revisor aparecem aqui depois de cada De/Para.
          </p>
        } @else {
          <app-dictionary-review-list
            [proposals]="reviewProposals()"
            [canManage]="canManage()"
            [locked]="isMutating()"
            [busyProposalId]="busyProposalId()"
            [busyGroupKey]="busyGroupKey()"
            (keepRequested)="keepProposal($event)"
            (revertRequested)="revertProposal($event)"
            (keepAllRequested)="keepAll($event)"
            (openDocumentRequested)="openDocumentRequested.emit($event)" />
        }
      </div>
    }
    @case ('pending') {
      <div class="dictionary__section" data-testid="dictionary-section-pending">
        <div class="dictionary__section-bar">
          <p class="dictionary__help" data-testid="dictionary-help-pending">
            Só o que não entra sozinho: definição nova de uma conta e o que o sistema não conseguiu
            gravar. As demais propostas já estão valendo e ficam em A revisar, onde
            <strong>Desfazer</strong> tira o texto do dicionário criando uma versão nova.
          </p>
          <div class="dictionary__filter">
            <app-select
              [label]="'Situação'"
              [labelSize]="'small'"
              [options]="proposalFilters"
              [hasFilter]="false"
              [ngModel]="proposalFilter()"
              (ngModelChange)="setProposalFilter($event)"
              data-testid="dictionary-proposals-filter" />
          </div>
        </div>

        @if (proposalsLoading()) {
          <div class="dictionary__loading">
            <app-cog-loading-spinner [size]="64" />
          </div>
        } @else if (proposalsError(); as message) {
          <p class="dictionary__error" role="alert">{{ message }}</p>
        } @else if (proposals().length === 0) {
          <p class="dictionary__empty" data-testid="dictionary-proposals-empty">
            @switch (proposalFilter()) {
              @case ('Pending') {
                Nenhuma proposta pendente. Aqui aparecem só as definições novas que o revisor
                sugerir e o que o sistema não conseguir gravar sozinho.
              }
              @case ('Applied') {
                Nenhuma proposta aplicada até agora.
              }
              @case ('Reverted') {
                Nenhuma mudança desfeita até agora.
              }
              @default {
                Nenhuma proposta recusada até agora.
              }
            }
          </p>
        } @else {
          <div class="dictionary__cards">
            @for (proposal of proposals(); track proposal.id) {
              <app-dictionary-proposal-card
                [proposal]="proposal"
                [canManage]="canManage()"
                [busy]="busyProposalId() === proposal.id"
                [locked]="isMutating()"
                (applyRequested)="applyProposal($event)"
                (editApplyRequested)="editAndApplyProposal($event)"
                (rejectRequested)="rejectProposal($event)"
                (openDocumentRequested)="openDocumentRequested.emit($event)" />
            }
          </div>
        }
      </div>
    }
    @case ('entries') {
      <div class="dictionary__section" data-testid="dictionary-section-entries">
        <p class="dictionary__help" data-testid="dictionary-help-entries">
          Cada conta do plano padrão como está na versão em uso, já com as propostas do revisor que
          entraram sozinhas. Abra uma conta para ler tudo e use <strong>Editar verbete</strong> para
          mudar o texto: salvar cria uma versão nova, usada no próximo De/Para.
        </p>
        @if (entriesLoading()) {
          <div class="dictionary__loading">
            <app-cog-loading-spinner [size]="64" />
          </div>
        } @else if (entriesError(); as message) {
          <p class="dictionary__error" role="alert">{{ message }}</p>
        } @else {
          <app-dictionary-entries-browser
            [entries]="activeEntries()"
            [versionNumber]="activeDetail()?.number ?? null"
            [canManage]="canManage()"
            [locked]="isMutating()"
            (editRequested)="editEntry($event)" />
        }
      </div>
    }
    @case ('versions') {
      <div class="dictionary__section" data-testid="dictionary-section-versions">
        <p class="dictionary__help" data-testid="dictionary-help-versions">
          Toda mudança no dicionário, de quem e quando; as do revisor aparecem como "Revisor
          (automático)", uma versão por documento. <strong>Voltar para esta versão</strong> não
          apaga nada: cria uma versão nova igual à escolhida. Para tirar só um texto, use Desfazer
          em A revisar.
        </p>
        @if (versionsLoading()) {
          <div class="dictionary__loading">
            <app-cog-loading-spinner [size]="64" />
          </div>
        } @else if (versionsError(); as message) {
          <p class="dictionary__error" role="alert">{{ message }}</p>
        } @else if (versions().length === 0) {
          <p class="dictionary__empty">
            Ainda não há versões gravadas. A primeira é criada sozinha a partir do arquivo do
            repositório quando o De/Para roda; use Atualizar para conferir.
          </p>
        } @else {
          <app-dictionary-version-timeline
            [versions]="versions()"
            [canManage]="canManage()"
            [locked]="isMutating()"
            (rollbackRequested)="rollbackTo($event)" />
        }
      </div>
    }
  }
</section>

<app-dictionary-reason-dialog />
<app-dictionary-proposal-apply-dialog />
<app-dictionary-entry-edit-dialog />
